import { Theme } from "@amx/ux-twoui-theming";
import { FontSizes, FontWeights, HighContrastSelector, IFontWeight } from "@fluentui/react";

export type TextAlignment = "right" | "center" | "left";

/** Interface for text type properties */
export interface FontRampProps {
    readonly fontSize: string;
    readonly fontWeight: IFontWeight;
    readonly color: string;
    readonly lineHeight: string;
    readonly fontFamily: string;
    readonly hoverColor: string;
}

/** Array of `TextTypes` values */
export const textTypes = [
    "xxlPageBanner",
    "xlPageHeader",
    "pageBanner",
    "pageTitle",
    "pageHeader",
    "statusHeader",
    "bodyLargeRegular",
    "paneHeader",
    "paneThemeTitle",
    "paneTitle",
    "bodyMediumThemeRegular",
    "bodyMediumRegular",
    "bodyMediumSecondaryRegular",
    "headerErrorText",
    "subjectTitle",
    "errorBlockIcon",
    "cautionYellowText",
    "bodySuccessText",
    "bodyThemeDarkText",
    "bodyThemeText",
    "headerText",
    "bodyTertiaryText",
    "bodySecondaryText",
    "bodyPrimaryText",
    "metadataText",
    "captionText",
    "superscript"
] as const;

/** Type of `Text` variation */
export type TextTypes = typeof textTypes[number];

/** Array of `HeadingTypes` values */
export const headingTypes = [
    "xxlPageBanner",
    "xlPageHeader",
    "pageBanner",
    "pageTitle",
    "pageHeader",
    "statusHeader",
    "paneHeader",
    "paneThemeTitle",
    "paneTitle",
    "subjectTitle",
    "headerErrorText",
    "headerText"
] as const;

/** Type of `Heading` variation */
export type HeadingTypes = typeof headingTypes[number];

/** Array of font weight values */
export const fontWeightOverrides = ["semi-bold", "bold"] as const;

/** Type of font weight values  */
export type FontWeightOverride = typeof fontWeightOverrides[number];

/** Object of font weights to actual weight values */
export const FontWeightOverridesMapper: { [K in FontWeightOverride]: IFontWeight } = {
    "semi-bold": FontWeights.semibold,
    "bold": FontWeights.bold
};

/** Array of possible color overrides */
export const colorOverrides = ["disabled", "error", "white"] as const;

/** String literal union type of color overrides */
export type ColorOverride = typeof colorOverrides[number];

/** Color override types to actual colors */
export const getColorOverride = (theme: Theme, override: ColorOverride) => {

    const overrideMapper: { readonly [key in ColorOverride]: string | undefined } = {
        "disabled": theme.palette.neutralTertiary,
        "error": theme.palette.redDark,
        "white": theme.palette.neutralHigh
    };

    return overrideMapper[override];
};

/** Line heights for varying font sizes */
export const LineHeights = {
    // Small is for font sizes 42px and below
    "standard": "1.4",
    // Large is for font sizes 64px and above
    "large": "1.2"
};

/** Default size, weight, and color for each variant */
export const getDefaultFontRampStyles = (theme: Theme, variant: TextTypes | HeadingTypes): FontRampProps => {

    const fontRampStyles: { readonly [key in TextTypes | HeadingTypes]: FontRampProps } = {
        xxlPageBanner: {
            fontSize: "80px",
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.large,
            fontFamily: theme.fonts?.large.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        xlPageHeader: {
            fontSize: "64px",
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.large,
            fontFamily: theme.fonts?.large.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        pageBanner: {
            fontSize: FontSizes.superLarge,
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.large.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        pageTitle: {
            fontSize: FontSizes.xxLargePlus,
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.large.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        pageHeader: {
            fontSize: FontSizes.xxLarge,
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.large.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        statusHeader: {
            fontSize: FontSizes.xLargePlus,
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        bodyLargeRegular: {
            fontSize: FontSizes.xLargePlus,
            fontWeight: FontWeights.regular,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        paneHeader: {
            fontSize: FontSizes.xLarge,
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        paneThemeTitle: {
            fontSize: FontSizes.large,
            fontWeight: FontWeights.semibold,
            color: theme.palette.themePrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.themePrimary
        },
        paneTitle: {
            fontSize: FontSizes.large,
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        bodyMediumThemeRegular: {
            fontSize: FontSizes.large,
            fontWeight: FontWeights.regular,
            lineHeight: LineHeights.standard,
            color: theme.palette.themePrimary,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.themePrimary
        },
        bodyMediumRegular: {
            fontSize: FontSizes.large,
            fontWeight: FontWeights.regular,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        bodyMediumSecondaryRegular: {
            fontSize: FontSizes.large,
            fontWeight: FontWeights.regular,
            color: theme.palette.neutralSecondary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralSecondary
        },
        headerErrorText: {
            fontSize: FontSizes.mediumPlus,
            fontWeight: FontWeights.semibold,
            color: theme.semanticColors.errorBlockIcon,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.semanticColors.errorBlockIcon
        },
        subjectTitle: {
            fontSize: FontSizes.mediumPlus,
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        errorBlockIcon: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.regular,
            color: theme.semanticColors.errorBlockIcon,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.semanticColors.errorBlockIcon
        },
        cautionYellowText: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.regular,
            color: theme.semanticColors.cautionYellow,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.semanticColors.cautionYellow
        },
        bodySuccessText: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.regular,
            color: theme.semanticColors.messageWebSuccess,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.semanticColors.messageWebSuccess
        },
        bodyThemeDarkText: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.semibold,
            color: theme.palette.themeDarkAlt,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.themeDarkAlt
        },
        bodyThemeText: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.regular,
            color: theme.palette.themePrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.themeDark
        },
        headerText: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.semibold,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        bodyTertiaryText: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.regular,
            color: theme.palette.neutralTertiary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralTertiary
        },
        bodySecondaryText: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.regular,
            color: theme.palette.neutralSecondary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralSecondary
        },
        bodyPrimaryText: {
            fontSize: FontSizes.medium,
            fontWeight: FontWeights.regular,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.medium.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        metadataText: {
            fontSize: FontSizes.small,
            fontWeight: FontWeights.regular,
            color: theme.palette.neutralSecondary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.small.font!,
            hoverColor: theme.palette.neutralSecondary
        },
        captionText: {
            fontSize: FontSizes.xSmall,
            fontWeight: FontWeights.regular,
            color: theme.palette.neutralPrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.small.font!,
            hoverColor: theme.palette.neutralPrimary
        },
        superscript: {
            fontSize: FontSizes.xSmall,
            fontWeight: FontWeights.regular,
            color: theme.palette.themePrimary,
            lineHeight: LineHeights.standard,
            fontFamily: theme.fonts?.small.font!,
            hoverColor: theme.palette.themePrimary
        }
    };

    return fontRampStyles[variant];
};

/** Retrieves font styles with respect to state */
export const getFontRampStyle = (theme: Theme, variant: TextTypes | HeadingTypes, fontWeightOverride?: FontWeightOverride, colorOverride?: ColorOverride) => {
    const defaultFontRampStyles = getDefaultFontRampStyles(theme, variant);

    const fontRampStylesWithOverride = {
        ...defaultFontRampStyles,
        fontWeight: fontWeightOverride ? FontWeightOverridesMapper[fontWeightOverride] : defaultFontRampStyles.fontWeight,
        color: colorOverride ? getColorOverride(theme, colorOverride) : defaultFontRampStyles.color,
        fontSize: defaultFontRampStyles.fontSize,
        lineHeight: defaultFontRampStyles.lineHeight
    };

    return {
        ...fontRampStylesWithOverride,
        // Fluent bug tracking 28552796
        selectors: {
            [HighContrastSelector]: {
                color: "windowtext",
            },
        },
    };
};
